import type { ReactNode } from 'react';
import type { FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { FIELD_HELPERS, FIELD_LABELS } from '@/features/startups/constants';
import { FileUploadField } from '@/components/common/FileUploadField';
import type { StartupProfileData } from '../../api/startup-profile.api';
import { STARTUP_PROFILE_STRINGS } from './startup-profile.strings';
import type { StartupProfileFormValues } from './schema';

const S = UI_STRINGS.STARTUP_PROFILE;

interface SectionProps {
  /** View mode only: extra header content, e.g. the create wizard review's Edit link. */
  headerRight?: ReactNode;
  mode: 'view' | 'edit';
  data: StartupProfileData;
  watch?: UseFormWatch<StartupProfileFormValues>;
  setValue?: UseFormSetValue<StartupProfileFormValues>;
  errors?: FieldErrors<StartupProfileFormValues>;
  onUploadingChange?: (isUploading: boolean) => void;
}

/**
 * Company Logo card (MYN-101, AC UI-1): view renders the stored logo, edit
 * uploads a replacement. Before this the logo could only be set in the create
 * wizard and never changed afterwards.
 *
 * Read and write are different fields and must not be confused: `data.logoUrl`
 * is a Backend-resolved presigned URL (MYN-175), safe as an `<img src>` and
 * never a valid thing to send back; `logoFileKey` is the raw S3 key the upload
 * flow produces and the only logo field the PATCH DTO accepts.
 */
export function LogoSection({
  mode,
  data,
  watch,
  setValue,
  errors,
  onUploadingChange,
  headerRight,
}: SectionProps) {
  if (mode === 'view' || !watch || !setValue) {
    return (
      <SectionCard title={STARTUP_PROFILE_STRINGS.COMPANY_LOGO} headerRight={headerRight}>
        {data.logoUrl ? (
          // Not the `Avatar` primitive: it substitutes initials when there is
          // no image, which would hide the "not added" state the sibling
          // sections deliberately show. Sized to match the circle dropzone the
          // edit branch renders in its place.
          <img
            src={data.logoUrl}
            alt={`${data.companyName} ${STARTUP_PROFILE_STRINGS.COMPANY_LOGO}`}
            className="size-16 rounded-full object-cover"
          />
        ) : (
          <p className="text-slate text-p6">{S.NOT_ADDED}</p>
        )}
      </SectionCard>
    );
  }

  // `logoFileKey` untouched (`undefined`) means the stored logo still stands,
  // so the control's done state comes from the fetched `logoUrl`. Once the
  // member has uploaded (a key) or removed (`''`), the form value is the
  // truth - reading `data.logoUrl` as a fallback there would keep the done
  // state up after a Remove and the member could never clear the logo.
  const logoFileKey = watch('logoFileKey');
  const hasLogo = logoFileKey === undefined ? Boolean(data.logoUrl) : Boolean(logoFileKey);

  return (
    <SectionCard title={STARTUP_PROFILE_STRINGS.COMPANY_LOGO}>
      <FileUploadField
        label={FIELD_LABELS.logo}
        hint={FIELD_HELPERS.logo}
        kind="logo"
        accept="image/png,image/jpeg"
        variant="circle"
        uploaded={hasLogo}
        error={errors?.logoFileKey?.message}
        onUploaded={(result) => setValue('logoFileKey', result.fileKey, { shouldDirty: true })}
        // The logo is optional (`docs/reference/data-fields.md`), so clearing
        // it is allowed. `''` is the clear signal the whole form shares with
        // the pitch deck - `toApiPayload` turns it into what the Backend
        // reads as a clear.
        onClear={() => setValue('logoFileKey', '', { shouldDirty: true })}
        onUploadingChange={onUploadingChange}
      />
    </SectionCard>
  );
}
